'use client';

import { useCallback, useEffect } from 'react';

import { useContextTabsContext } from '../context/ContextTabsContext';

/** Registers unsaved state against the current Context Tab and cleans it up. */
export function useContextTabDirtyState(isDirty: boolean) {
  const context = useContextTabsContext();
  const tabId = context?.currentRouteTabId;
  const setTabDirty = context?.setTabDirty;
  const clearContextTabDirty = context?.clearContextTabDirty;

  useEffect(() => {
    if (!setTabDirty || !tabId) return;
    setTabDirty(tabId, isDirty);
    return () => {
      setTabDirty(tabId, false);
    };
  }, [isDirty, setTabDirty, tabId]);

  useEffect(() => {
    if (!isDirty) return;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [isDirty]);

  return useCallback(
    (after?: () => void) => {
      if (clearContextTabDirty && tabId) {
        clearContextTabDirty(tabId, after);
      } else {
        if (setTabDirty && tabId) setTabDirty(tabId, false);
        after?.();
      }
    },
    [clearContextTabDirty, setTabDirty, tabId],
  );
}
